<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <BaseForm
      ref="form"
      v-bind="form"
      novalidate
      class="screen__form screen__form_no_padding"
      @submit="onFormSubmit"
      @show:terms="showTermsModal"
      @show:rules="showRulesModal"
    />
    <template #root>
      <ModalTerms
        v-if="isTermsModalVisible"
        class="screen__overlay"
        @close="hideTermsModal"
      />
      <ModalRules
        v-if="isRulesModalVisible"
        class="screen__overlay"
        @close="hideRulesModal"
      />
    </template>
  </BaseScreen>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';

import screenMixin from '@/mixins/screen';
import ModalTerms from '@/components/ModalTerms.vue';
import ModalRules from '@/components/ModalRules.vue';
import BaseForm from '@/components/BaseForm.vue';
import FieldEmail from '@/components/FieldEmail.vue';
import FieldPhone from '@/components/FieldPhone.vue';
import FieldName from '@/components/FieldName.vue';

import CONSTANTS from '@/helpers/constants';

const {
  SCREEN_READY,
  ACTION_OPEN_MODAL_RULES,
  ACTION_OPEN_MODAL_TERMS,
  ACTION_SUBMIT,
} = CONSTANTS;

export default {
  name: 'BuyFormScreen',
  mixins: [screenMixin],
  components: {
    ModalTerms,
    ModalRules,
    BaseForm,
  },
  data() {
    return {
      isTermsModalVisible: false,
      isRulesModalVisible: false,
    };
  },
  computed: {
    ...mapState('cart', [
      'tickets',
      'buyer',
      'order',
    ]),
    ...mapGetters('cart', [
      'isOrderActive',
      'sum',
    ]),
    title() {
      return this.$t('titles.form');
    },
    pendingText() {
      return this.$t('pending.order');
    },
    error() {
      return {
        title: this.$t('titles.badConnection'),
        text: this.$t('texts.orderFailed'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    form() {
      const sum = this.sum > 0 ? this.$options.filters.sum(this.sum, true) : '';
      const submitButtonText = this.$t('actions.pay') + sum;
      return {
        fields: [
          {
            id: 'name',
            component: FieldName,
            defaultValue: this.buyer?.name,
          },
          {
            id: 'phone',
            component: FieldPhone,
            defaultValue: this.buyer?.phone || '+7',
          },
          {
            id: 'email',
            component: FieldEmail,
            defaultValue: this.buyer?.email,
          },
        ],
        hasTermsText: true,
        submitButtonText,
      };
    },
  },
  mounted() {
    this.initActions();
  },
  methods: {
    ...mapMutations('cart', [
      'setBuyer',
      'setOrder',
    ]),
    ...mapActions('cart', [
      'createOrder',
    ]),
    async bookTickets() {
      try {
        this.showPending();

        const order = await this.createOrder({
          tickets: this.tickets,
          buyer: this.buyer,
        });

        this.setOrder(order);
        this.goNext();
      } catch {
        this.showError();
      }
    },
    onFormSubmit(data) {
      if (this.isPending) return;
      this.setBuyer(data);
      if (this.isOrderActive) {
        this.goNext();
      } else {
        this.bookTickets();
      }
    },
    showTermsModal() {
      this.isTermsModalVisible = true;
    },
    hideTermsModal() {
      this.isTermsModalVisible = false;
    },
    showRulesModal() {
      this.isRulesModalVisible = true;
    },
    hideRulesModal() {
      this.isRulesModalVisible = false;
    },
    setFieldFocus() {
      return this.$refs.form?.setFocus();
    },
    submitForm() {
      return this.$refs.form?.submit();
    },
    goNext() {
      this.$router.push(this.$router.options.paths.payInstruction);
    },
    initActions() {
      const rootActions = [
        [SCREEN_READY, this.setFieldFocus],
        [ACTION_OPEN_MODAL_RULES, this.showRulesModal],
        [ACTION_OPEN_MODAL_TERMS, this.showTermsModal],
        [ACTION_SUBMIT, this.submitForm],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
